<script setup lang="ts">
import { computed } from 'vue';
import TagBadge from './TagBadge.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
// Lazy: one tooltip per header-tagged chip, per card, across a whole list.
import { LazyTooltip } from '@/components/ui/tooltip';
import { tagKind, useTagCatalog, type TagKind } from '@/composables/api/useTagCatalog';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/**
 * Renders a record's tags as colored chips for the bottom of a card. Accepts the
 * raw `tags` value — a to-many relation that may arrive as an array of nested
 * models (serialized as `display_string`), a single model, or be absent — and
 * renders nothing when there are no usable tags. Used by the search-result cards
 * (every entity that carries a `tags` relation) and reusable for any other card.
 *
 * The embedded tag payload is a bare {id, display_string}; the shared tag
 * catalog resolves each id to its flags (font color: track = blue, artist =
 * brown, user = green) and its header/group tag — a tag with a parent_tag_id
 * gets a tooltip naming that header tag.
 *
 * Chips render grouped and sorted: non-user tags A→Z, then user tags A→Z
 * (case-insensitive). Tags the catalog can't resolve (still loading, id-less,
 * or unknown) sort in the non-user group by label. With `showRating` (Live
 * Track rows) the row leads with a rating chip — the row's `rating` value
 * (A1/B1/C1) or NR for Not Rated — and a tag whose name duplicates the rating
 * is dropped so it doesn't render twice.
 */
const props = withDefaults(
    defineProps<{
        tags?: unknown;
        /** Lead with a rating chip (Live Track rows) — renders NR when `rating` is empty. */
        showRating?: boolean;
        /** The row's rating value; only read when `showRating` is set. Never trusted to be a string. */
        rating?: unknown;
        /**
         * The quick-search fields this row matched (SAU-591), passed in by the
         * search page. A tag or rating match is highlighted in its chip.
         */
        matchedFields?: readonly string[];
    }>(),
    { tags: undefined, showRating: false, rating: undefined, matchedFields: () => [] }
);

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
const tagsMatched = computed(() => props.matchedFields.includes('tags.display_string'));
const ratingMatched = computed(() => props.matchedFields.includes('rating'));

interface TagLike {
    id?: number;
    // Nested model fields serialize as display_string; fall back to name just in case.
    display_string?: string;
    name?: string;
}

function tagLabel(tag: TagLike): string {
    const label = tag.display_string ?? tag.name ?? '';
    return label.trim();
}

const { tagsById, parentTagName } = useTagCatalog();

/** The rating chip's text: the trimmed/uppercased rating, or NR for Not Rated. */
const displayRating = computed<string>(() => {
    if (typeof props.rating !== 'string') {
        return 'NR';
    }
    const trimmed = props.rating.trim().toUpperCase();
    return trimmed === '' ? 'NR' : trimmed;
});

const normalizedTags = computed<
    Array<{ key: string; label: string; kind: TagKind | null; headerTag: string | null }>
>(() => {
    const raw = props.tags;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const chips = list
        .filter((tag): tag is TagLike => !!tag && typeof tag === 'object')
        // Key by position (prefixed with any real id for readability). A bare
        // `tag.id ?? index` collided an id-less tag's index with a sibling's real
        // id, producing duplicate v-for keys; the `index:` prefix can never collide.
        .map((tag, index) => {
            const catalogTag = typeof tag.id === 'number' ? tagsById.value.get(tag.id) : undefined;
            return {
                key: `${index}:${tag.id ?? ''}`,
                label: tagLabel(tag),
                kind: catalogTag ? tagKind(catalogTag) : null,
                headerTag: parentTagName(catalogTag),
            };
        })
        .filter((tag) => tag.label !== '')
        // The rating chip already shows this value; a same-named tag would render twice.
        .filter((tag) => !props.showRating || tag.label.toUpperCase() !== displayRating.value);

    const byLabel = (a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    const userTags = chips.filter((tag) => tag.kind === 'user').sort(byLabel);
    const nonUserTags = chips.filter((tag) => tag.kind !== 'user').sort(byLabel);

    return [...nonUserTags, ...userTags];
});
</script>

<template>
    <div
        v-if="normalizedTags.length || showRating"
        class="mt-2 flex flex-wrap gap-1"
        data-testid="card-tags"
    >
        <SearchHighlight v-if="showRating" :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="ratingMatched">
            <TrackRatingBadge :rating="displayRating" />
        </SearchHighlight>
        <SearchHighlight
            v-for="tag in normalizedTags"
            :key="tag.key"
            :name="LIST_HIGHLIGHT"
            :terms="highlightTerms"
            :active="tagsMatched"
        >
            <LazyTooltip v-if="tag.headerTag" side="top">
                <!-- Trigger wraps a plain span (not the component) so Reka's
                     pointer handlers land on a native element — mirroring the
                     LZP status-dot tooltip, the reference implementation. -->
                <span class="inline-flex cursor-default" data-testid="tag-badge-tooltip-trigger">
                    <TagBadge :name="tag.label" :kind="tag.kind" />
                </span>
                <template #content>{{ tag.headerTag }}</template>
            </LazyTooltip>
            <TagBadge v-else :name="tag.label" :kind="tag.kind" />
        </SearchHighlight>
    </div>
</template>
